import { supabase } from './supabase';
import type { Json } from '../types/database.types';

// Frontera con lo que introdujo la migración 195 (borrador de informe formal).
//
// Este módulo nació con un cast sobre `supabase.rpc`/`from` porque los tipos se
// generan contra la BD real y la 195 no estaba aplicada. Ya lo está y los tipos
// están regenerados, así que el cast se ha retirado: lo que ves aquí lo verifica
// el compilador contra el esquema de verdad.
//
// Lo que NO hace este módulo: reimplementar reglas. Quién puede publicar, en qué
// orden y con qué requisitos lo decide el trigger de la 195. Esto solo traduce
// los códigos de error a frases que el usuario entiende.

export type EvaluationStatus = 'draft' | 'published';

// `status` es una columna text con CHECK, no un enum de Postgres, así que el
// generador la tipa `string`. Se estrecha aquí, en la frontera, y no en cada
// pantalla.
const asStatus = (s: string): EvaluationStatus => (s === 'draft' ? 'draft' : 'published');

// Las dos RPC devuelven `jsonb`, que en los tipos generados es `Json`: un union
// que incluye escalares y arrays. Estrecharlo a objeto es la lectura correcta —
// no un cast — y deja el resto del módulo trabajando con un mapa.
const asRecord = (data: Json | null): Record<string, Json | undefined> =>
  data !== null && typeof data === 'object' && !Array.isArray(data) ? data : {};

// Los embeds `profiles!monitor_id(...)` los tipa supabase-js como ARRAY, porque
// el generador marca `isOneToOne: false` en toda FK que no sea única. PostgREST
// devuelve un OBJETO cuando la clave está en la fila que embebe, que es este
// caso. El repo resuelve el desajuste con doble cast (`as unknown as`, patrón
// ADR-0020); aquí se normaliza en runtime, que además aguanta si algún día la
// inferencia cambia de opinión.
const one = <T>(v: T | T[] | null | undefined): T | null =>
  Array.isArray(v) ? (v[0] ?? null) : (v ?? null);

export interface ExistingEvaluation {
  id: string;
  status: EvaluationStatus;
  report_number: string | null;
  /**
   * Con qué catálogo se puntuó. Quien carga esto en un formulario TIENE que
   * mirarlo: abrir un borrador de otra versión en el formulario de hoy no
   * enseña lo que hay dentro, y guardarlo lo sobrescribe.
   */
  catalog_version: number | null;
  detailed_scores: Json | null;
  overall_comment: string | null;
  position_primary: string | null;
  position_secondary: string | null;
}

export interface SaveDraftParams {
  studentId: string;
  monitorId: string;
  periodStart: string;
  periodEnd: string;
  detailedScores: Json;
  overallComment: string | null;
  positionPrimary: string | null;
  positionSecondary: string | null;
  catalogVersion: number;
}

export interface ObjectiveReview {
  id: string;
  status: string;
  comment: string;
}

export interface NewObjectiveInput {
  pillar: string;
  description: string;
}

/** Resultado normalizado: `ok` o un código de error ya traducido a mensaje. */
export interface RpcOutcome {
  ok: boolean;
  code?: string;
  message?: string;
  evaluationId?: string;
  reportNumber?: string | null;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return 'sin fecha';
  return new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Los mensajes dicen QUÉ HACER, no solo que ha fallado. El caso del informe
// anterior sin publicar es el que más falta hace: sin nombres ni fechas, quien
// lo lea no tiene forma de saber qué informe le está estorbando.
function describe(payload: Record<string, Json | undefined>): string {
  const code = String(payload.error ?? '');
  switch (code) {
    case 'already_published':
      return `Este informe ya está publicado${
        payload.report_number ? ` (${payload.report_number})` : ''
      }. No se puede sobrescribir.`;
    case 'period_overlap':
      return `Ya hay un informe publicado de este alumno con este entrenador que cubre parte de ese periodo${
        payload.report_number ? ` (${payload.report_number}` : ''
      }${
        payload.report_number
          ? `, ${fmtDate(payload.period_start as string)} → ${fmtDate(payload.period_end as string)})`
          : ''
      }. Ajusta las fechas.`;
    case 'earlier_draft_pending':
      return `Hay un informe anterior de este alumno sin publicar (${fmtDate(
        payload.blocker_period_start as string
      )} → ${fmtDate(payload.blocker_period_end as string)}, firmado por ${
        payload.blocker_monitor_name ?? 'otro entrenador'
      }). Publícalo o bórralo antes de publicar éste.`;
    case 'not_found':
      return 'El informe ya no existe.';
    default:
      return 'No se pudo completar la operación.';
  }
}

/** Informe existente para (alumno, entrenador, periodo), sea borrador o publicado. */
export async function findEvaluation(
  studentId: string,
  monitorId: string,
  periodStart: string
): Promise<{ informe: ExistingEvaluation | null; error: string | null }> {
  const { data, error } = await supabase
    .from('formal_evaluations')
    .select(
      'id, status, report_number, catalog_version, detailed_scores, overall_comment, position_primary, position_secondary'
    )
    .eq('student_id', studentId)
    .eq('monitor_id', monitorId)
    .eq('period_start', periodStart)
    .maybeSingle();
  // ⚠️ AQUÍ EL `null` NO ERA «no hay informe»: era «no pude mirar».
  //
  // Quien llama usa este `null` para decidir si empieza uno nuevo, y empezar
  // uno nuevo VACÍA el formulario. Con la lectura caída, el entrenador se
  // encontraba las 60 subcategorías en blanco sobre un borrador que sí existe,
  // y al guardar lo pisaba. Un fallo de red no puede costar el trabajo de otro.
  if (error) return { informe: null, error: error.message || 'Error desconocido' };
  if (!data) return { informe: null, error: null };
  return { informe: { ...data, status: asStatus(data.status) }, error: null };
}

/**
 * Borradores del alumno SIN publicar, de cualquier entrenador. Se usa para
 * avisar antes de empezar: por el orden cronológico, un borrador anterior
 * bloqueará la publicación de éste.
 */
export async function listOpenDrafts(studentId: string): Promise<{
  borradores: {
    id: string;
    period_start: string;
    period_end: string | null;
    monitor_name: string;
  }[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('formal_evaluations')
    .select('id, period_start, period_end, monitor:profiles!monitor_id(full_name)')
    .eq('student_id', studentId)
    .eq('status', 'draft')
    .order('period_start');

  // Este aviso existe para que nadie empiece un informe que otro va a bloquear
  // al publicar. Sin él —y una lista vacía por fallo de lectura es «sin él»—
  // el entrenador puntúa 60 subcategorías y se entera al final.
  if (error) return { borradores: [], error: error.message || 'Error desconocido' };
  return {
    borradores: (data ?? []).map((d) => ({
      id: d.id,
      period_start: d.period_start,
      period_end: d.period_end,
      monitor_name: one(d.monitor)?.full_name ?? 'entrenador desconocido',
    })),
    error: null,
  };
}

export interface TrainerHistoryRow {
  id: string;
  student_id: string;
  period_start: string;
  period_end: string | null;
  status: EvaluationStatus;
  report_number: string | null;
  /** Sin esto, quien recompute las medias lo hará con el catálogo equivocado. */
  catalog_version: number | null;
  detailed_scores: Json | null;
  student: { full_name: string } | null;
}

/**
 * Historial del entrenador. Incluye sus BORRADORES, marcados: son suyos y tiene
 * que poder volver a ellos — es el camino por el que continúa lo que empezó
 * dirección. Ordenados por periodo descendente, como antes.
 */
export async function listTrainerHistory(
  monitorId: string
): Promise<{ historial: TrainerHistoryRow[]; error: string | null }> {
  const { data, error } = await supabase
    .from('formal_evaluations')
    .select(
      'id, student_id, period_start, period_end, status, report_number, catalog_version, detailed_scores, student:profiles!student_id(full_name)'
    )
    .eq('monitor_id', monitorId)
    .order('period_start', { ascending: false });
  // Su historial incluye los BORRADORES: es el camino por el que vuelve a lo
  // que dejó a medias. Una lista vacía por un fallo de lectura le dice que no
  // tiene ninguno.
  if (error) return { historial: [], error: error.message || 'Error desconocido' };
  return {
    historial: (data ?? []).map((r) => ({
      ...r,
      status: asStatus(r.status),
      student: one(r.student),
    })),
    error: null,
  };
}

export async function saveEvaluationDraft(p: SaveDraftParams): Promise<RpcOutcome> {
  const { data, error } = await supabase.rpc('save_evaluation_draft', {
    p_student_id: p.studentId,
    p_monitor_id: p.monitorId,
    p_period_start: p.periodStart,
    p_period_end: p.periodEnd,
    p_detailed_scores: p.detailedScores,
    p_overall_comment: p.overallComment,
    p_position_primary: p.positionPrimary,
    p_position_secondary: p.positionSecondary,
    p_catalog_version: p.catalogVersion,
  });

  if (error) return { ok: false, message: error.message };
  const payload = asRecord(data);
  if (payload.ok === true) {
    return { ok: true, evaluationId: payload.evaluation_id as string };
  }
  return { ok: false, code: String(payload.error ?? ''), message: describe(payload) };
}

export async function publishEvaluation(
  evaluationId: string,
  reviews: ObjectiveReview[],
  newObjectives: NewObjectiveInput[]
): Promise<RpcOutcome> {
  const { data, error } = await supabase.rpc('publish_evaluation', {
    p_evaluation_id: evaluationId,
    p_objective_reviews: reviews,
    p_new_objectives: newObjectives.filter((o) => o.description.trim()),
  });

  if (error) return { ok: false, message: error.message };
  const payload = asRecord(data);
  if (payload.ok === true) {
    return {
      ok: true,
      evaluationId: payload.evaluation_id as string,
      reportNumber: (payload.report_number as string) ?? null,
    };
  }
  return { ok: false, code: String(payload.error ?? ''), message: describe(payload) };
}

/**
 * Borra un borrador. No hay RPC: las políticas de la mig 128 ya dejan borrar al
 * entrenador que lo firma y a dirección, que son exactamente los dos que deben
 * poder. Un informe PUBLICADO no se borra desde aquí.
 */
export async function deleteEvaluationDraft(evaluationId: string): Promise<RpcOutcome> {
  const { error } = await supabase
    .from('formal_evaluations')
    .delete()
    .eq('id', evaluationId)
    .eq('status', 'draft');
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}
